/**
 * Test Edit Trigger Component
 * 
 * Allows programmatic triggering of proposed edits for testing purposes.
 * This component can be used by AI agents or test utilities to trigger
 * edits without requiring manual chat interaction.
 * 
 * Usage:
 * 1. Import this component in your test page
 * 2. Call triggerProposedEdit() with edit data
 * 3. The edit will appear in the chat pane and diff preview modal
 */

import { toast } from 'sonner';

export interface TestEditData {
  documentId: string;
  original: string;
  proposed: string;
  filename?: string;
  title?: string;
  explanation?: string;
  frontmatter?: Record<string, any> | null;
}

import { API_BASE_URL } from '@/lib/api';

/**
 * Trigger a proposed edit programmatically
 * 
 * This function:
 * 1. Calls the test API endpoint to generate the edit payload
 * 2. Dispatches a CustomEvent that the chat pane listens for
 * 3. Opens the diff preview modal automatically
 */
export async function triggerProposedEdit(editData: TestEditData): Promise<boolean> {
  try {
    console.log('[TestEditTrigger] Triggering proposed edit:', editData);

    let proposedEdit: any;

    // Try to call the test API endpoint first
    try {
      const response = await fetch(`${API_BASE_URL}/api/test/trigger-edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editData),
      });

      if (!response.ok) {
        // Check if response is HTML (404 page) instead of JSON
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          console.warn('[TestEditTrigger] Route not found, using fallback mode (server may need restart)');
          throw new Error('ROUTE_NOT_FOUND');
        }
        
        try {
          const error = await response.json();
          throw new Error(error.error || 'Failed to trigger edit');
        } catch (e) {
          if (e instanceof Error && e.message.includes('JSON')) {
            throw new Error('ROUTE_NOT_FOUND');
          }
          throw e;
        }
      }

      const result = await response.json();
      proposedEdit = result.proposedEdit;

      if (!proposedEdit) {
        throw new Error('No proposed edit returned from API');
      }
    } catch (apiError) {
      // Fallback: Generate the edit payload client-side if API is unavailable
      if (apiError instanceof Error && apiError.message === 'ROUTE_NOT_FOUND') {
        console.log('[TestEditTrigger] Using client-side fallback mode');
        
        // Import diff library for client-side computation
        const { diffLines } = await import('diff');
        const changes = diffLines(editData.original, editData.proposed);
        
        const added: number[] = [];
        const removed: number[] = [];
        let lineNum = 1;
        
        changes.forEach(change => {
          const lines = change.value.split('\n');
          const lineCount = change.value.endsWith('\n') ? lines.length - 1 : lines.length;
          
          if (change.added) {
            for (let i = 0; i < lineCount; i++) {
              if (lines[i] && lines[i].trim().length > 0) {
                added.push(lineNum + i);
              }
            }
          } else if (change.removed) {
            for (let i = 0; i < lineCount; i++) {
              if (lines[i] && lines[i].trim().length > 0) {
                removed.push(lineNum + i);
              }
            }
          }
          
          if (!change.added) {
            lineNum += lineCount;
          }
        });
        
        proposedEdit = {
          type: 'proposed_edit',
          documentId: editData.documentId,
          filename: editData.filename || 'document.md',
          title: editData.title || 'Test Document',
          original: editData.original,
          proposed: editData.proposed,
          frontmatter: editData.frontmatter || null,
          explanation: editData.explanation || 'Test edit triggered programmatically',
          changedLines: {
            added,
            removed,
            modified: [],
            changed_at: new Date().toISOString(),
            changed_by: 'test',
            change_status: 'pending',
            requires_acceptance: true
          },
          changeStatus: 'pending',
          requiresAcceptance: true
        };
      } else {
        throw apiError;
      }
    }

    // Dispatch a CustomEvent that simulates an SSE event
    // The chat pane listens for this event type
    const event = new CustomEvent('test-proposed-edit', {
      detail: proposedEdit
    });

    window.dispatchEvent(event);

    // Also dispatch as a message event (for compatibility with chat pane SSE handler)
    // We'll simulate the SSE format
    const sseEvent = new MessageEvent('message', {
      data: JSON.stringify(proposedEdit)
    });

    // Store the proposed edit in a global location that chat pane can access
    // This is a workaround since we can't directly inject into the SSE stream
    (window as any).__testProposedEdit = proposedEdit;

    // Trigger the chat pane's event handler by dispatching a custom event
    // that matches what the SSE handler expects
    window.dispatchEvent(new CustomEvent('chat-stream-event', {
      detail: proposedEdit
    }));

    toast.success('Test edit triggered', {
      description: `${proposedEdit.filename}: +${proposedEdit.changedLines?.added?.length || 0} -${proposedEdit.changedLines?.removed?.length || 0} lines`
    });

    return true;
  } catch (error) {
    console.error('[TestEditTrigger] Error:', error);
    toast.error('Failed to trigger edit', {
      description: error instanceof Error ? error.message : 'Unknown error'
    });
    return false;
  }
}

/**
 * Create a test edit that adds a line at the end of a document
 */
export function createAddLineTestEdit(
  documentId: string,
  currentContent: string,
  newLine: string,
  filename?: string
): TestEditData {
  return {
    documentId,
    original: currentContent,
    proposed: currentContent + (currentContent.endsWith('\n') ? '' : '\n') + newLine + '\n',
    filename: filename || 'document.md',
    title: 'Test Document',
    explanation: `Test edit: Adding line "${newLine}"`
  };
}

/**
 * Create a test edit that modifies existing content
 */
export function createModifyTestEdit(
  documentId: string,
  originalContent: string,
  findText: string,
  replaceText: string,
  filename?: string
): TestEditData {
  const proposed = originalContent.replace(findText, replaceText);
  
  return {
    documentId,
    original: originalContent,
    proposed,
    filename: filename || 'document.md',
    title: 'Test Document',
    explanation: `Test edit: Replacing "${findText}" with "${replaceText}"`
  };
}

/**
 * Create a test edit that removes a line
 */
export function createRemoveLineTestEdit(
  documentId: string,
  currentContent: string,
  lineToRemove: string,
  filename?: string
): TestEditData {
  const lines = currentContent.split('\n');
  const filtered = lines.filter(line => line.trim() !== lineToRemove.trim());
  const proposed = filtered.join('\n');
  
  return {
    documentId,
    original: currentContent,
    proposed,
    filename: filename || 'document.md',
    title: 'Test Document',
    explanation: `Test edit: Removing line "${lineToRemove}"`
  };
}

